import {
  ColDef,
  ColDefField,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterFunc,
} from 'ag-grid-community';

import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
  GridValueGetterParams,
} from '@mui/x-data-grid';

export function adaptGridColDef<
  R extends GridValidRowModel = any,
  V = any,
  F = V,
  TData = any,
>(muiColDef: GridColDef<R, V, F>): ColDef<TData> {
  const agColDef: Partial<ColDef<TData>> = {
    field: muiColDef.field as ColDefField<TData, any>,
    headerName: muiColDef.headerName,
    tooltipField: muiColDef.field as ColDefField<TData, any>,

    width: muiColDef.width,
    minWidth: muiColDef.minWidth,
    maxWidth: muiColDef.maxWidth,
    flex: muiColDef.flex,

    sortable: muiColDef.sortable,
  };

  if (muiColDef.valueGetter) {
    agColDef.valueGetter = ((params) => {
      const gridValueGetterParams: Partial<GridValueGetterParams<V, R>> = {
        value: params.node?.data
          ? (params?.node?.data as Record<string, any>)[
              params?.colDef?.field as string
            ]
          : undefined,
        row: params.data as unknown as R,
        field: params.colDef.field as string,
        api: undefined, // AG Grid doesn't have a direct equivalent
      };

      return muiColDef?.valueGetter?.(
        gridValueGetterParams as GridValueGetterParams<V, R>,
      );
    }) as ValueGetterFunc<TData>;
  }

  if (muiColDef.valueFormatter) {
    agColDef.valueFormatter = ((params: ValueFormatterParams<TData, any>) => {
      return String(
        muiColDef?.valueFormatter?.({ value: params.value } as any),
      );
    }) as ValueFormatterFunc<TData>;
  }

  if (muiColDef?.valueSetter) {
    agColDef.valueSetter = (params) => {
      const newData = muiColDef?.valueSetter?.({
        value: params.newValue,
        row: params.data as unknown as R,
      } as any);

      if (
        params.data &&
        typeof params.data === 'object' &&
        newData &&
        typeof newData === 'object'
      ) {
        Object.assign(params.data, newData);
      }
      return true;
    };
  }

  if (muiColDef.renderCell) {
    agColDef.cellRenderer = (params: any) => {
      const gridRenderCellParams: Partial<GridRenderCellParams<V, R, F>> = {
        value: params.value,
        row: params.data as unknown as R,
        field: params.colDef.field as string,
        api: undefined, // No direct equivalent
        id: (params.data as any)?.id,
        formattedValue: params.valueFormatted || params.value,
        hasFocus: params.cellFocused,
        tabIndex: params.rowIndex,
      };

      return muiColDef.renderCell!(
        gridRenderCellParams as GridRenderCellParams<V, R, F>,
      );
    };
  }

  return agColDef as ColDef<TData>;
}

export function adaptGridColDefs<
  R extends GridValidRowModel = any,
  V = any,
  F = V,
  TData = any,
>(muiColDefs: GridColDef<R, V, F>[]): ColDef<TData>[] {
  return muiColDefs.map((colDef) => adaptGridColDef<R, V, F, TData>(colDef));
}
